<template>
  <aside
    class="collapsible-aside"
    :class="[
      { 'collapsible-aside--collapsed': isCollapsed },
      `collapsible-aside--${side}`
    ]"
    :style="asideStyle"
  >
    <div class="collapsible-aside__rail">
      <button
        class="collapsible-aside__rail-handle"
        type="button"
        :title="`${isCollapsed ? 'Show' : 'Hide'} ${panelName}`"
        @click="toggle"
      >
        <span
          v-if="isCollapsed"
          class="collapsible-aside__rail-text"
        >{{
          panelName
        }}</span>
        <i
          class="collapsible-aside__rail-icon"
          :class="railIconClass"
        />
      </button>
    </div>

    <div class="collapsible-aside__content">
      <slot />
    </div>
  </aside>
</template>

<script>
import VueTypes from 'vue-types'

export default {
  name: 'CollapsibleAside',

  props: {
    defaultCollapsed: VueTypes.bool.def(false),
    expandedWidth: VueTypes.string.def('256px'),
    collapsedWidth: VueTypes.string.def('40px'),
    panelName: VueTypes.string.isRequired,
    side: VueTypes.oneOf(['left', 'right']).def('right')
  },

  data () {
    return { isCollapsed: this.defaultCollapsed }
  },

  computed: {
    /**
     * Builds the aside's inline width styles and exposes the rail width as a CSS var.
     *
     * @returns {object} Style object bound to the aside element.
     */
    asideStyle () {
      const width = this.isCollapsed ? this.collapsedWidth : this.expandedWidth
      const railWidth = this.side === 'left' ? '32px' : this.collapsedWidth

      return {
        width,
        flexBasis: width,
        '--rail-width': railWidth
      }
    },

    /**
     * Resolves the rail chevron class, pointing toward the panel's next move (mirrored for the left side).
     *
     * @returns {string} Element UI icon class for the rail handle.
     */
    railIconClass () {
      if (this.side === 'left') {
        return this.isCollapsed
          ? 'el-icon-d-arrow-right'
          : 'el-icon-d-arrow-left'
      }

      return this.isCollapsed ? 'el-icon-d-arrow-left' : 'el-icon-d-arrow-right'
    }
  },

  methods: {
    /**
     *
     */
    toggle () {
      this.isCollapsed = !this.isCollapsed
    }
  }
}
</script>

<style lang="scss" scoped>
$aside-motion: 160ms cubic-bezier(0.2, 0.8, 0.2, 1);
$rail-motion: 120ms cubic-bezier(0.2, 0.8, 0.2, 1);

$rail-handle-min-width: spacing(7/2); // 28px if spacing(1) = 8px
$rail-handle-min-height: spacing(7); // 56px
$rail-handle-collapsed-height: spacing(17); // 136px
$rail-handle-expanded-width: spacing(4); // 32px
$rail-handle-expanded-height: spacing(6); // 48px
$rail-handle-left-width: spacing(7/2); // 28px (smaller left button)
$rail-handle-left-height: spacing(5); // 40px
$rail-handle-radius: spacing(99);

$rail-label-font-size: 11px;
$rail-icon-font-size: spacing(2);

.collapsible-aside {
  position: relative;
  display: grid;
  flex: 0 0 auto;
  // Single full-height row; rail and panel are pinned to it (see `grid-row`).
  grid-template-rows: minmax(0, 1fr);
  grid-template-columns: var(--rail-width, 40px) minmax(0, 1fr);
  min-width: var(--rail-width, 40px);
  height: 100%;
  overflow: hidden;
  background: transparent;
  // border-left: $border-divider;
  // box-shadow: $shadow-default;

  transition: width $aside-motion, flex-basis $aside-motion;
}

.collapsible-aside__rail {
  display: flex;
  // Pinned to row 1 so auto-placement can't spill the panel into a second row.
  grid-row: 1;
  grid-column: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: transparent;
  pointer-events: none;
}

.collapsible-aside__rail-handle {
  display: inline-flex;
  gap: spacing(1);
  align-items: center;
  justify-content: center;
  min-width: $rail-handle-min-width;
  min-height: $rail-handle-min-height;
  padding: spacing(1);
  color: $grey-dark;
  background: #fff;
  border: $border-divider;
  border-radius: $rail-handle-radius;
  box-shadow: $shadow-default;
  cursor: pointer;
  transition:
    background-color $rail-motion,
    border-color $rail-motion,
    color $rail-motion,
    transform $rail-motion,
    box-shadow $rail-motion;
  pointer-events: auto;

  &:hover,
  &:focus-visible {
    background-color: rgba(64, 158, 255, 0.06);
    border-color: rgba(64, 158, 255, 0.35);
  }

  &:focus {
    outline: 0;
  }

  &:focus-visible {
    outline: spacing(1/4) solid rgba(64, 158, 255, 0.7);
    outline-offset: spacing(1/4);
  }
}

.collapsible-aside--collapsed .collapsible-aside__rail-handle {
  flex-direction: column;
  min-height: $rail-handle-collapsed-height;
}

.collapsible-aside:not(.collapsible-aside--collapsed) .collapsible-aside__rail-handle {
  width: $rail-handle-expanded-width;
  min-width: $rail-handle-expanded-width;
  height: $rail-handle-expanded-height;
  min-height: $rail-handle-expanded-height;
  padding: 0;
  border-radius: $rail-handle-radius 0 0 $rail-handle-radius;
  transform: translateX(spacing(1/8));
}

.collapsible-aside__rail-text {
  display: block;
  font-weight: $font-weight-bold;
  font-size: $rail-label-font-size;
  line-height: 1;
  letter-spacing: 0.18em;
  white-space: nowrap;
  text-transform: uppercase;
  transform: rotate(180deg);
  writing-mode: vertical-rl;
}

.collapsible-aside__rail-icon {
  font-size: $rail-icon-font-size;
  line-height: 1;
}

.collapsible-aside__content {
  // Pinned to the rail's row so the two sit side by side.
  grid-row: 1;
  grid-column: 2;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  visibility: visible;
  opacity: 1;
  transition: opacity $rail-motion, visibility $rail-motion;
}

// Menu fills the full width; the rail overlays it so the button adds no width.
.collapsible-aside--left {
  grid-template-columns: minmax(0, 1fr);
}

.collapsible-aside--left .collapsible-aside__content {
  grid-row: 1;
  grid-column: 1;
}

.collapsible-aside--left .collapsible-aside__rail {
  position: absolute;
  top: 0;
  right: 0;
  align-items: center;
  justify-content: flex-start;
  width: var(--rail-width, 44px);
  height: 100%;
}

// The overlaid rail seats the handle in the card's padding; 1px tucks it under the border.
.collapsible-aside--left:not(.collapsible-aside--collapsed) .collapsible-aside__rail-handle {
  width: $rail-handle-left-width;
  min-width: $rail-handle-left-width;
  height: $rail-handle-left-height;
  min-height: $rail-handle-left-height;
  border-radius: 0 $rail-handle-radius $rail-handle-radius 0;
  transform: translateX(-(spacing(1/8)));
}

// Collapsed: icons get their own column with the button beside them on the right.
.collapsible-aside--left.collapsible-aside--collapsed {
  grid-template-columns: minmax(0, 1fr) var(--rail-width, 44px);
}

.collapsible-aside--left.collapsible-aside--collapsed .collapsible-aside__rail {
  position: static;
  grid-row: 1;
  grid-column: 2;
  justify-content: flex-start;
}

// Uncollapse button matches the expanded handle's size, seated beside the icons.
.collapsible-aside--left.collapsible-aside--collapsed .collapsible-aside__rail-handle {
  flex-direction: row;
  width: $rail-handle-left-width;
  min-width: $rail-handle-left-width;
  height: $rail-handle-left-height;
  min-height: $rail-handle-left-height;
  padding: 0;
  border-radius: 0 $rail-handle-radius $rail-handle-radius 0;
  transform: translateX(-(spacing(1/8)));
}

// Seats the filters handle flush against the panel's edge instead of centred in its column.
.collapsible-aside--right:not(.collapsible-aside--collapsed) .collapsible-aside__rail {
  justify-content: flex-end;
}

.collapsible-aside--right.collapsible-aside--collapsed .collapsible-aside__content {
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
}

.collapsible-aside--left.collapsible-aside--collapsed /deep/ .the-aside-left {
  width: 100%;
  padding: spacing(2) 0 spacing(4) 0;
}

.collapsible-aside--left.collapsible-aside--collapsed /deep/ .the-aside-left__nav {
  align-items: center;
  padding: spacing(2) spacing(1);
}

.collapsible-aside--left.collapsible-aside--collapsed /deep/ .the-aside-left__nav-menu {
  align-items: center;
  width: 100%;
}

.collapsible-aside--left.collapsible-aside--collapsed /deep/ .the-aside-left__nav-menu-title,
.collapsible-aside--left.collapsible-aside--collapsed /deep/ .the-aside-left__nav-menu-item-arrow-icon,
.collapsible-aside--left.collapsible-aside--collapsed /deep/ .the-aside-left-version {
  display: none;
}

.collapsible-aside--left.collapsible-aside--collapsed /deep/ .the-aside-left__nav-menu-item {
  justify-content: center;
  width: 100%;
  overflow: hidden;
  font-size: 0;
  white-space: nowrap;
}

.collapsible-aside--left.collapsible-aside--collapsed /deep/ .the-aside-left__nav-menu-item-icon {
  flex: 0 0 auto;
  width: spacing(7/2);
  height: spacing(7/2);
  margin-right: 0;
}

// The FAQ icon is a PNG (others are SVGs) and reads larger; trim it to match.
.collapsible-aside--left.collapsible-aside--collapsed /deep/ .the-aside-left__nav-menu-item-icon--faq {
  width: spacing(3);
  height: spacing(3);
}

// The left rail shows only the chevron when collapsed; the panel name is hidden.
.collapsible-aside--left .collapsible-aside__rail-text {
  display: none;
}
</style>
